import { Instance, flow, types } from 'mobx-state-tree';

import { getBooksStudentPages } from '@common/api/bookStudentPages';

import { getBooks } from '../../api/books';
import { IProgramModel } from '../Programs/Program';
import TableData, { ITableDataQuery } from '../TableData';
import Book, { IBookModel, IBookWithProgress } from './Book';

export type IBooksGroupedByProgram = {
	sharedProgram: IProgramModel;
	books: IBookWithProgress[];
};

export type BooksGroupedByProgramsReturnType = (IBookWithProgress | IBooksGroupedByProgram)[];

const Books = types
	.compose(
		TableData,
		types.model({
			data: types.optional(types.array(Book), []),
			service_id: types.maybe(types.number),
			studentId: types.maybe(types.number),
			isEnabled: types.maybe(types.boolean),
		}),
	)
	.named('Books')
	.volatile(() => ({
		book_type_id: 1,
		progressData: {} as { [key: number]: string },
	}))
	.views((self) => ({
		get booksWithProgress() {
			return self.data
				.filter((book: IBookModel) => book.book_type.id === self.book_type_id)
				.map((book) => ({
					...book,
					progress: self.progressData[book.id] || '0.00',
				}));
		},
	}))
	.actions((self) => ({
		setBookTypeId(id: number) {
			self.book_type_id = id;
		},
		_fetch: (query: ITableDataQuery, options: any) =>
			getBooks(
				{
					...query,
					...(self.service_id && { service_id: self.service_id }),
					...(self.studentId && { student_id: self.studentId }),
					...(self.isEnabled && { is_enabled: 1 }),
				},
				options,
			),
		fetchBooksProgress: flow(function* (studentId: number) {
			self.progressData = yield getBooksStudentPages({
				student_id: studentId,
				limit: 99,
			});
		}),
		booksGroupedByPrograms: (): BooksGroupedByProgramsReturnType => {
			const mergedBooks = self.booksWithProgress;
			if (mergedBooks?.length <= 0) return [];

			// Створюємо об'єкт для зберігання програм і відповідних книг
			const programBooksMap: Record<number, { program: any; books: typeof mergedBooks }> = {};

			// Проходимося по всіх книгах і групуємо їх за програмами
			mergedBooks.forEach((book) => {
				book.programs.forEach((program: any) => {
					if (!programBooksMap[program.id]) {
						programBooksMap[program.id] = { program, books: [] };
					}
					programBooksMap[program.id].books.push(book);
				});
			});

			// Створюємо масив для зберігання результату
			const result: BooksGroupedByProgramsReturnType = [];

			// Визначаємо, які книги мають спільні програми і групуємо їх
			const groupedBooks = new Set();
			Object.values(programBooksMap).forEach(({ program, books }) => {
				if (books.length > 1) {
					// Якщо книги мають спільну програму, додаємо їх як масив з додатковим полем sharedProgram
					result.push({
						sharedProgram: program, // Додаємо спільну програму
						books,
					});
					books.forEach((book) => groupedBooks.add(book));
				}
			});

			// Додаємо книги, які не мають спільних програм
			mergedBooks.forEach((book) => {
				if (!groupedBooks.has(book)) {
					result.push(book);
				}
			});

			return result;
		},
	}));

export default Books;

export interface IBooksModel extends Instance<typeof Books> {}
